




























































































































import { Component, Watch } from 'vue-property-decorator';
import MixinPage from '@/plugins/podolib/services/helper/mixins/page';
import { get } from 'lodash';
import { ValidationProvider } from 'vee-validate/dist/types/components/Provider';
import { navigatorLang, getCountries, Country } from '@/plugins/i18n';
import axios from 'axios';

import type { TForm } from '@/plugins/podolib/lib/types';
import type { TEntity } from '@/plugins/podolib/services/entities/Entity';
import type { Doctor } from '@/plugins/podolib/services/doctor';

import AuthCard from '@/components/auth/AuthCard.vue';
import { getProcessEnv } from '@/lib/build';
import { Severity } from '@sentry/types';
import { Document } from '@/plugins/podolib/services/doctor';

type TFields = {
  firstName: {
    value: string;
  };
  lastName: {
    value: string;
  };
  phone: {
    code: {
      value: string;
    };
    value: {
      value: string;
    };
  };
  professions: {
    value: string[];
  };
  newsletters: {
    value: boolean;
  };
};

interface AuthCreateDoctorProfile extends MixinPage {
  ready: boolean;
  doNotTrack: boolean;
  form: TForm<TFields>;
  doctor: TEntity<Doctor>;
  countries: Country[];
}

@Component<AuthCreateDoctorProfile>({
  components: { AuthCard },
  async created() {
    if (this.doctor) {
      await this.$router.push('/');
    }

    try {
      if (!this.doNotTrack) {
        // Fetch lookup...
        const res1 = await axios.get(getProcessEnv('CLOUD_URL') + 'rest/ip-info', {
          headers: {
            authorization: `Bearer ${this.$pdc.cognito!.accessToken}`,
          },
        });

        this.form.fields.phone.code.value = res1.data.country_code;
      }
    } catch (e) {
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Warning);
    } finally {
      // Select default value for country code input
      if (!this.form.fields.phone.code.value) this.form.fields.phone.code.value = 'US';
    }

    this.ready = true;
  },
})
export default class PageAuthCreateDoctorProfile extends MixinPage {
  $refs!: {
    card: InstanceType<typeof AuthCard> & {
      $refs: {
        observer: InstanceType<typeof ValidationProvider>;
      };
    };
    phoneCode: any;
  };

  ready = false;

  form: TForm<TFields> = {
    fields: {
      firstName: {
        value: '',
      },
      lastName: {
        value: '',
      },
      phone: {
        code: {
          value: '',
        },
        value: {
          value: '',
        },
      },
      professions: {
        value: [],
      },
      newsletters: {
        value: false,
      },
    },
    pending: false,
    error: null,
    success: false,
  };

  get countries() {
    return getCountries({ excludeNoDial: true });
  }

  get professions() {
    const keys = [
      'neurologist',
      'orthopaedic_surgeon',
      'osteopath',
      'physiotherapist',
      'podiatrist',
      'sports_coach',
      'other',
    ];
    return keys.map((x) => ({
      text: this.$t(`commons.professions.${x}`),
      value: x,
    }));
  }

  get doNotTrack() {
    return window.navigator.doNotTrack === '1';
  }

  get doctor() {
    return this.$pdc.doctor!.current;
  }

  get newDoctor(): Partial<Doctor> {
    const _n: Partial<Doctor> = {
      userId: get(this.$pdc.cognito!, 'user.attributes.sub'),
      firstName: this.form.fields.firstName.value,
      lastName: this.form.fields.lastName.value,
      professions: this.form.fields.professions.value,
      phone: null as any,
      offices: {
        items: [],
        selected: null!,
      },
      config: {
        lang: navigatorLang(),
        measurementUnit: 'metric',
        sidebar: 'default',
      },
      newsletters: {
        consented: this.form.fields.newsletters.value,
      },
      // TODO : edit when a document is available
      consents: [
        {
          document: Document.DPA,
          links: [
            { lang: 'en', link: this.$pdc.helper!.fromCdn('other', `legal/DPA/20200818_DPA_DS_ProX_en.pdf`) },
            { lang: 'fr', link: this.$pdc.helper!.fromCdn('other', `legal/DPA/20200818_DPA_DS_ProX_fr.pdf`) },
            { lang: 'de', link: this.$pdc.helper!.fromCdn('other', `legal/DPA/20200818_DPA_DS_ProX_de.pdf`) },
          ],
          version: new Date('2021-03-29T00:00:00+02:00'),
          consented: true,
          date: new Date(),
          tacit: true,
          ip: window.ip,
        },
        {
          document: Document.TOU,
          links: [
            { lang: 'en', link: this.$pdc.helper!.fromCdn('other', `legal/TOU/20191105_TOU_PodoSmart_en.pdf`) },
            { lang: 'fr', link: this.$pdc.helper!.fromCdn('other', `legal/TOU/20191105_TOU_PodoSmart_fr.pdf`) },
            { lang: 'de', link: this.$pdc.helper!.fromCdn('other', `legal/TOU/20191105_TOU_PodoSmart_de.pdf`) },
          ],
          version: new Date('2021-03-29T00:00:00+02:00'),
          consented: true,
          date: new Date(),
          tacit: true,
          ip: window.ip,
        },
      ],
    };
    if (this.form.fields.phone.value.value) {
      _n.phone = {
        code: this.form.fields.phone.code.value,
        value: this.form.fields.phone.value.value,
      };
    }
    return _n;
  }

  @Watch('form.fields.phone.code.value')
  onPhoneCodeChanged(newValue: string) {
    if (newValue) this.$refs.phoneCode.blur();
  }

  async createDoctorProfile() {
    // Avoid duplication of the process
    if (this.form.pending) return;
    try {
      // Validate form...
      const isValid = await this.$refs.card.$refs.observer.validate();
      // If form is invalid, stop
      if (!isValid) return;

      // Reset flags
      this.form.success = false;
      this.form.error = null;
      // Set 'pending' flag
      this.form.pending = true;

      // Try to create doctor ...
      const doctor: TEntity<Doctor> = await this.$pdc.repository!.emit('create', 'doctors', this.newDoctor);
      // Analytics
      this.$pdc.analytics?.actions.trackSuccess('User ProfileCreated', this.newDoctor);
      // Save doctor
      this.$store.commit('pdc/doctor/SET_CURRENT', doctor);
      // Fetch entities
      await this.$pdc.entities!.sync();
      // Set 'success' flag
      this.form.success = true;
      // Send global event
      this.$pdc.bus!.emit('doctor:created');
      // Redirect to home...
      await this.$router.push({ name: 'home' });
    } catch (e) {
      // Analytics
      this.$pdc.analytics?.actions.trackFailure('User ProfileCreated', e, this.newDoctor);
      // Set 'error' flag
      this.form.error = e;
      // Sentry
      this.$sentry.captureMessage(e.message, Severity.Error);
    } finally {
      // Reset 'pending' flag
      this.form.pending = false;
    }
  }
}
